import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Megaphone, Sparkles } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import { useSetMarketplaceRole, type MarketplaceRole } from "@/hooks/useMarketplace";
import { AdvisorLogoIcon } from "@/components/icons/AdvisorLogo";
import { MARKETPLACE_NAME } from "./constants";

const OPTIONS: Array<{
  role: MarketplaceRole;
  icon: typeof Megaphone;
  titleKey: string;
  bodyKey: string;
  bulletKeys: string[];
}> = [
  {
    role: "BRAND",
    icon: Megaphone,
    titleKey: "marketplace.role.brand.title",
    bodyKey: "marketplace.role.brand.body",
    bulletKeys: [
      "marketplace.role.brand.b1",
      "marketplace.role.brand.b2",
      "marketplace.role.brand.b3",
    ],
  },
  {
    role: "CREATOR",
    icon: Sparkles,
    titleKey: "marketplace.role.creator.title",
    bodyKey: "marketplace.role.creator.body",
    bulletKeys: [
      "marketplace.role.creator.b1",
      "marketplace.role.creator.b2",
      "marketplace.role.creator.b3",
    ],
  },
];

/**
 * Side selection. Deliberately a hard fork rather than a toggle: the choice is
 * permanent on the server (the escrow ledger has no notion of a party changing
 * what it is), so the copy says so before the click rather than after.
 */
export default function RolePicker() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const { setRole, loading, error } = useSetMarketplaceRole();
  const [selected, setSelected] = useState<MarketplaceRole | null>(null);

  const [failed, setFailed] = useState<string | null>(null);

  const confirm = async () => {
    if (!selected) return;
    setFailed(null);
    try {
      const updated = await setRole(selected);
      // Push the server's own values into AuthContext before navigating.
      // AuthContext holds its user in React state and never subscribes to
      // GetMe, so without this MarketplaceRoute still sees a null role and
      // redirects right back here — which looks like the button doing nothing.
      updateUser({
        marketplaceRole: updated.marketplaceRole,
        onboardingCompleted: updated.onboardingCompleted,
      });
      // Both sides land on the thing they must fill in before anything else
      // works. A brand sent to /descubrir could browse the catalogue and then
      // fail at the first campaign, because every brand-side query needs a
      // BrandProfile that nothing had yet created.
      navigate(updated.marketplaceRole === "BRAND" ? "/marca" : "/perfil", { replace: true });
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center px-6 py-12">
      <div className="flex items-center gap-2.5 mb-8">
        <AdvisorLogoIcon size="sm" className="text-steel-600" />
        <span className="font-bold text-sm text-steel-600 tracking-tight">{MARKETPLACE_NAME}</span>
      </div>

      <h1 className="text-2xl font-bold text-neutral-900 text-center">
        {t("marketplace.role.title")}
      </h1>
      <p className="text-sm text-neutral-500 mt-2 text-center max-w-md">
        {t("marketplace.role.subtitle")}
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mt-8 w-full max-w-3xl">
        {OPTIONS.map(({ role, icon: Icon, titleKey, bodyKey, bulletKeys }) => {
          const active = selected === role;
          return (
            <button
              key={role}
              type="button"
              onClick={() => setSelected(role)}
              className={`text-left bg-white border rounded-card p-6 transition-all cursor-pointer ${
                active
                  ? "border-steel-600 ring-2 ring-steel-600/20 shadow-sm"
                  : "border-neutral-200 hover:border-neutral-300 hover:shadow-sm"
              }`}
            >
              <div
                className={`w-10 h-10 rounded-control flex items-center justify-center mb-3 ${
                  active ? "bg-steel-600 text-white" : "bg-neutral-100 text-neutral-500"
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>
              <p className="font-semibold text-sm text-neutral-900">{t(titleKey)}</p>
              <p className="text-xs text-neutral-500 mt-1 leading-relaxed">{t(bodyKey)}</p>
              <ul className="mt-3 space-y-1">
                {bulletKeys.map((k) => (
                  <li key={k} className="text-xs text-neutral-600 flex items-start gap-1.5">
                    <span className="text-steel-600 mt-px">•</span>
                    {t(k)}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-neutral-400 mt-6 text-center max-w-md">
        {t("marketplace.role.permanentNotice")}
      </p>

      {(failed || error) && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-control px-3 py-2 mt-4 max-w-md text-center">
          {failed ?? error?.message}
        </p>
      )}

      <button
        type="button"
        onClick={confirm}
        disabled={!selected || loading}
        className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-button bg-gradient-to-r from-steel-600 to-steel-700 text-white text-sm font-medium hover:from-steel-500 hover:to-steel-600 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {t("marketplace.role.continue")}
      </button>

      {/* A greyed-out CTA with no reason next to it reads as a broken button. */}
      <p className="text-xs text-neutral-400 mt-2 h-4">
        {!selected && t("marketplace.role.selectFirst")}
      </p>
    </div>
  );
}
